/**
 * DetailDrawer — 대시보드 타일 클릭 시 우측 Drawer 로 상세 목록 표시 (Task 4.1)
 *
 * 기존 admin 목록 API 를 재사용한다. metric 별로 어떤 엔드포인트/파라미터를 쓰는지
 * config 에 정의하고, metric 이 null 이면 fetch 하지 않는다 (useApiFetch 에 null 경로 전달).
 *
 * 날짜 범위(from/to) 지원 현황:
 *  - 신규 가입자(users) / AI 사진 생성·실패(ai-activity-history) → startDate/endDate 지원 → 기간 반영됨
 *  - 발행 청첩장 / 만료 임박 / 활성 청첩장(invitations) → 날짜 파라미터 미지원 → status 필터만 적용 (기간 무관)
 *  - 신규 문의 / 미처리 문의(contact-inquiries) → 날짜 미지원 → status 필터만 적용
 *  - 주문번호 등록(activation-codes) → 날짜 미지원 → status=USED 만 적용
 * 날짜 미지원 엔드포인트는 헤더에 "전체 기간" 안내 문구를 표시한다.
 */

import { useMemo } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { Drawer, Table, Spinner, Alert, Badge, formatDate, formatNumber, type TableColumn } from '@/ui'
import { StatusBadge } from '@/components/StatusBadge'
import styles from './DetailDrawer.module.css'

// Drawer 를 여는 타일 키. 비율 타일(전환율)은 목록으로 표현이 어려워 제외.
export type DrawerMetric =
  | 'newUsers'
  | 'smartstoreRegistrations'
  | 'aiGenerations'
  | 'publishedInvitations'
  | 'newInquiries'
  | 'openInquiries'
  | 'aiFailRate24h'
  | 'expiringSoon'
  | 'activeInvitations'

// --- 엔드포인트별 응답 타입 (각 페이지에서 쓰는 모양의 최소 부분만 정의) ---

interface UserRow {
  id: string
  email: string
  name: string | null
  createdAt: string
}
interface UsersResponse {
  users: UserRow[]
}

interface InvitationRow {
  id: string
  user: { id: string; email: string; name: string | null }
  groomName: string | null
  brideName: string | null
  status: string
  createdAt: string
  publishedAt: string | null
  expiresAt: string | null
}
interface InvitationsResponse {
  invitations: InvitationRow[]
}

interface InquiryRow {
  id: string
  name: string
  email: string
  category: string
  status: string
  createdAt: string
}
interface InquiriesResponse {
  items: InquiryRow[]
}

interface AiActivityRow {
  id: string
  status: string
  modelId: string | null
  style: string | null
  createdAt: string
  user: { email: string; name: string | null }
}
interface AiActivityResponse {
  activities: AiActivityRow[]
}

interface ActivationCodeRow {
  id: string
  code: string
  type: string
  status: string
  usedByEmail: string | null
  usedAt: string | null
  smartstoreOrderId: string | null
}
interface ActivationCodesResponse {
  codes: ActivationCodeRow[]
}

// metric → 응답 타입 매핑 (path 빌더가 반환하는 데이터 모양 구분용)
type ResponseByMetric = {
  newUsers: UsersResponse
  smartstoreRegistrations: ActivationCodesResponse
  aiGenerations: AiActivityResponse
  publishedInvitations: InvitationsResponse
  newInquiries: InquiriesResponse
  openInquiries: InquiriesResponse
  aiFailRate24h: AiActivityResponse
  expiringSoon: InvitationsResponse
  activeInvitations: InvitationsResponse
}

// metric → 테이블 행 타입 (응답의 배열 원소)
interface ResponseByMetricRow {
  newUsers: UserRow
  smartstoreRegistrations: ActivationCodeRow
  aiGenerations: AiActivityRow
  publishedInvitations: InvitationRow
  newInquiries: InquiryRow
  openInquiries: InquiryRow
  aiFailRate24h: AiActivityRow
  expiringSoon: InvitationRow
  activeInvitations: InvitationRow
}

// 각 metric 설정: 제목 · 경로 빌더 · 행 추출 · 컬럼 · 날짜지원 여부.
// rows/columns 는 같은 metric 의 응답 타입에서 추출되므로 행 타입 R 이 일치한다.
interface MetricConfig<K extends DrawerMetric, R> {
  title: string
  /** 날짜 파라미터를 지원하는 엔드포인트인지 (false 면 "전체 기간" 안내 표시) */
  dateBounded: boolean
  path: (from: string, to: string) => string
  rows: (data: ResponseByMetric[K]) => R[]
  columns: TableColumn<R>[]
}

const PAGE_SIZE = 50

// --- 컬럼 정의 ---

const userColumns: TableColumn<UserRow>[] = [
  { key: 'name', label: '이름', render: (r) => r.name ?? '-', width: '120px' },
  { key: 'email', label: '이메일' },
  { key: 'createdAt', label: '가입일', width: '140px', render: (r) => formatDate(r.createdAt, 'yyyy.M.d HH:mm') },
]

const invitationColumns: TableColumn<InvitationRow>[] = [
  {
    key: 'couple',
    label: '신랑·신부',
    width: '160px',
    render: (r) => `${r.groomName ?? '-'} ♥ ${r.brideName ?? '-'}`,
  },
  { key: 'user', label: '유저', render: (r) => r.user.name ?? r.user.email },
  { key: 'status', label: '상태', width: '110px', render: (r) => <StatusBadge status={r.status} /> },
  {
    key: 'expiresAt',
    label: '만료일',
    width: '140px',
    render: (r) => (r.expiresAt ? formatDate(r.expiresAt, 'yyyy.M.d') : '-'),
  },
  {
    key: 'createdAt',
    label: '생성일',
    width: '140px',
    render: (r) => formatDate(r.createdAt, 'yyyy.M.d'),
  },
]

const inquiryColumns: TableColumn<InquiryRow>[] = [
  { key: 'createdAt', label: '접수', width: '140px', render: (r) => formatDate(r.createdAt, 'yyyy.M.d HH:mm') },
  { key: 'status', label: '상태', width: '90px', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'category', label: '유형', width: '100px' },
  { key: 'name', label: '문의자', render: (r) => `${r.name} (${r.email})` },
]

const aiActivityColumns: TableColumn<AiActivityRow>[] = [
  { key: 'createdAt', label: '일시', width: '140px', render: (r) => formatDate(r.createdAt, 'yyyy.M.d HH:mm') },
  { key: 'status', label: '상태', width: '110px', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'user', label: '유저', render: (r) => r.user?.name ?? r.user?.email ?? '-' },
  { key: 'modelId', label: '모델', width: '140px', render: (r) => r.modelId ?? '-' },
  { key: 'style', label: '스타일', width: '140px', render: (r) => r.style ?? '-' },
]

const activationCodeColumns: TableColumn<ActivationCodeRow>[] = [
  { key: 'code', label: '코드', width: '160px' },
  { key: 'type', label: '유형', width: '160px' },
  { key: 'status', label: '상태', width: '100px', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'usedByEmail', label: '사용자', render: (r) => r.usedByEmail ?? '-' },
  { key: 'smartstoreOrderId', label: '주문번호', width: '160px', render: (r) => r.smartstoreOrderId ?? '-' },
  { key: 'usedAt', label: '사용일', width: '140px', render: (r) => (r.usedAt ? formatDate(r.usedAt, 'yyyy.M.d HH:mm') : '-') },
]

// YYYY-MM-DD → 그 날 끝(23:59:59) ISO. ai-activity-history 는 ISO 를 받는다.
function toEndIso(ymd: string): string {
  return new Date(`${ymd}T23:59:59`).toISOString()
}
function toStartIso(ymd: string): string {
  return new Date(`${ymd}T00:00:00`).toISOString()
}

// --- metric 설정 맵 ---

// 각 항목의 행 타입 R 은 응답 타입에서 추론된다 (key 별로 다름).
const CONFIG: { [K in DrawerMetric]: MetricConfig<K, ResponseByMetricRow[K]> } = {
  newUsers: {
    title: '신규 가입자',
    dateBounded: true,
    path: (from, to) =>
      `/api/admin/users?page=1&pageSize=${PAGE_SIZE}&sortBy=createdAt&sortOrder=desc&startDate=${from}&endDate=${to}`,
    rows: (d) => d.users,
    columns: userColumns,
  },
  smartstoreRegistrations: {
    title: '주문번호 등록 (활성화 코드)',
    dateBounded: false,
    path: () => `/api/admin/activation-codes?page=1&pageSize=${PAGE_SIZE}&status=USED`,
    rows: (d) => d.codes,
    columns: activationCodeColumns,
  },
  aiGenerations: {
    title: 'AI 사진 생성',
    dateBounded: true,
    path: (from, to) =>
      `/api/admin/ai-activity-history?page=1&pageSize=${PAGE_SIZE}&type=generation&sortBy=createdAt&sortOrder=desc&startDate=${encodeURIComponent(toStartIso(from))}&endDate=${encodeURIComponent(toEndIso(to))}`,
    rows: (d) => d.activities,
    columns: aiActivityColumns,
  },
  publishedInvitations: {
    title: '발행 청첩장',
    dateBounded: false,
    path: () =>
      `/api/admin/invitations?page=1&pageSize=${PAGE_SIZE}&status=PUBLISHED&sortBy=publishedAt&sortOrder=desc`,
    rows: (d) => d.invitations,
    columns: invitationColumns,
  },
  newInquiries: {
    title: '신규 문의',
    dateBounded: false,
    path: () => `/api/admin/contact-inquiries?page=1&pageSize=${PAGE_SIZE}`,
    rows: (d) => d.items,
    columns: inquiryColumns,
  },
  openInquiries: {
    title: '미처리 문의',
    dateBounded: false,
    path: () => `/api/admin/contact-inquiries?page=1&pageSize=${PAGE_SIZE}&status=UNREAD`,
    rows: (d) => d.items,
    columns: inquiryColumns,
  },
  aiFailRate24h: {
    title: 'AI 실패 (최근)',
    dateBounded: false,
    path: () =>
      `/api/admin/ai-activity-history?page=1&pageSize=${PAGE_SIZE}&status=failed&sortBy=createdAt&sortOrder=desc`,
    rows: (d) => d.activities,
    columns: aiActivityColumns,
  },
  expiringSoon: {
    title: '만료 임박 청첩장',
    dateBounded: false,
    path: () =>
      `/api/admin/invitations?page=1&pageSize=${PAGE_SIZE}&status=PUBLISHED&sortBy=expiresAt&sortOrder=asc`,
    rows: (d) => d.invitations,
    columns: invitationColumns,
  },
  activeInvitations: {
    title: '활성 청첩장',
    dateBounded: false,
    path: () =>
      `/api/admin/invitations?page=1&pageSize=${PAGE_SIZE}&status=PUBLISHED&sortBy=createdAt&sortOrder=desc`,
    rows: (d) => d.invitations,
    columns: invitationColumns,
  },
}

interface DetailDrawerProps {
  metric: DrawerMetric | null
  from: string
  to: string
  onClose: () => void
}

// 유니온 인덱싱으로 좁혀지지 않는 부분을 한 곳에서 unknown 으로 처리하기 위한 헬퍼 타입.
interface AnyConfig {
  title: string
  dateBounded: boolean
  path: (from: string, to: string) => string
  rows: (data: unknown) => unknown[]
  columns: TableColumn<unknown>[]
}

export function DetailDrawer({ metric, from, to, onClose }: DetailDrawerProps) {
  // CONFIG[metric] 은 metric 별로 다른 R 을 갖는 유니온이라 호출부에서 다루기 까다롭다.
  // 같은 metric 에서 path·rows·columns 가 함께 묶여 있어 행 타입이 일치하므로 AnyConfig 로 통일한다.
  const config: AnyConfig | null = metric ? (CONFIG[metric] as unknown as AnyConfig) : null
  // metric 이 null 이면 path=null → fetch 안 함
  const path = config ? config.path(from, to) : null
  const { data, isLoading, error } = useApiFetch<unknown>(path)

  const rows = useMemo(() => {
    if (!config || !data) return []
    return config.rows(data)
  }, [config, data])

  // 테이블 컬럼이 많아 lg(600px)로는 잘려서, 콘텐츠가 다 보이도록 넓게 연다.
  return (
    <Drawer open={metric !== null} onClose={onClose} title={config?.title ?? ''} width="min(95vw, 1000px)">
      {config && !config.dateBounded && (
        <p className={styles.notice}>
          이 목록은 날짜 필터를 지원하지 않아 <strong>전체 기간</strong> 기준 최신 {formatNumber(PAGE_SIZE)}건을 보여줍니다.
        </p>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {isLoading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : (
        config && (
          // 컬럼·행 타입은 동일 metric 에서 추출되어 일치한다.
          <Table<unknown>
            columns={config.columns}
            data={rows}
            rowKey={(r) => (r as { id: string }).id}
            isLoading={false}
            emptyMessage="없음"
          />
        )
      )}
    </Drawer>
  )
}
